/**
 * Central API client for /service/iqevse-csms/* endpoints.
 *
 * CSRF (H-1): No synchronizer token — backend mitigates via SameSite session cookies,
 * CORS, Origin/Referer checks, and server-side session validation on every mutation.
 * Optional X-Requested-With on state-changing requests (REACT_APP_CSRF_HEADER_ENABLED=true)
 * triggers CORS preflight so simple cross-site forms cannot replay cookie-backed POSTs.
 * UI headers are not a security boundary.
 *
 * Transport (H-4): REACT_APP_BUILD_DOMAIN and production hosting use HTTPS.
 *
 * Rate limiting (H-7): Login brute-force protection is enforced by the auth service (429 / lockout).
 * UI adds session-scoped failed-attempt tracking in Login.jsx; not a security boundary.
 *
 * Input validation (H-6): Relative URLs and JSON params/bodies are sanitized before send.
 * Backend remains the authoritative validation boundary for injection and business rules.
 */
import axios from "axios";
import axiosRetry from "axios-retry";
import { getCookie } from "../lib/utils/cookieUtil";
import { handleUnauthorizedSession } from "../lib/utils/featureAccess";
import { assertSafeRelativeUrl, sanitizeRequestData } from "../lib/utils/apiValidation";
import { addLog } from "../lib/utils/persistentLogger";

const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"]);
// SECURITY FIX H-4: Enable CSRF protection by default
const CSRF_HEADER_ENABLED = process.env.REACT_APP_CSRF_HEADER_ENABLED !== "false";

class ApiClass {
  constructor() {
    this.axiosInstance = axios.create({
      baseURL: process.env.REACT_APP_BUILD_DOMAIN,
      timeout: 10000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    axiosRetry(this.axiosInstance, {
      retries: 3,
      retryDelay: retryCount => retryCount * 1000,
      retryCondition: error => {
        const url = error.config?.url || "";
        if (url.includes("/auth/v1/login")) return false;
        return error.response?.status === 504;
      },
    });

    this.axiosInstance.interceptors.request.use(
      config => {
        const method = (config.method || "get").toLowerCase();
        if (CSRF_HEADER_ENABLED && MUTATING_METHODS.has(method)) {
          config.headers["X-Requested-With"] = "XMLHttpRequest";
        }

        const token = getCookie("session_id");
        addLog("API Request", "Processing request", { url: config.url, method, sessionIdFound: !!token });

        if (token) {
          config.headers["Authorization"] = `_enlighten_4_session=${token}`;
          config.headers["e-auth-token"] = token;
        }

        const managerToken = getCookie("manager_token");
        if (managerToken) {
          config.headers["manager-token"] = managerToken;
        }

        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );
    this.axiosInstance.interceptors.response.use(
      response => {
        addLog("API Response", "Success", { url: response.config.url, status: response.status });
        return response;
      },
      error => {
        const status = error.response?.status;
        const url = error.config?.url || "";
        const isAuthRoute = url.includes("/auth/v1/login") || url.includes("/auth/v1/logout");

        addLog("API Response Error", "Error response received", { url, status, isAuthRoute });

        if ((status === 401 || status === 403) && !isAuthRoute) {
          addLog("API Response", "Unauthorized/Forbidden on non-auth route, calling handleUnauthorizedSession");
          handleUnauthorizedSession();
        }
        return Promise.reject(error);
      }
    );
  }

  async _request(method, url, data = {}, config = {}) {
    /* eslint-disable  no-unused-vars*/
    const { params, body, skipSanitize, ...axiosConfig } = config;

    assertSafeRelativeUrl(url);

    const payload = skipSanitize || data instanceof FormData ? data : sanitizeRequestData(data);

    const requestConfig = {
      method,
      url,
      ...axiosConfig,
      headers: { ...this.axiosInstance.defaults.headers, ...axiosConfig.headers },
    };

    if (method.toLowerCase() === "get" || method.toLowerCase() === "delete") {
      requestConfig.params = payload;
    } else {
      requestConfig.data = payload;
    }

    const response = await this.axiosInstance(requestConfig);
    return response.data;
  }

  get(url, params = {}, config = {}) {
    return this._request("get", url, params, config);
  }

  post(url, body = {}, config = {}) {
    return this._request("post", url, body, config);
  }

  patch(url, body = {}, config = {}) {
    return this._request("patch", url, body, config);
  }

  put(url, body = {}, config = {}) {
    return this._request("put", url, body, config);
  }

  delete(url, params = {}, config = {}) {
    return this._request("delete", url, params, config);
  }

  upload(url, formData, onUploadProgress) {
    return this._request("post", url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
      skipSanitize: true,
    });
  }
}

const Api = new ApiClass();
export default Api;
